import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FileText,
  Download,
  Calendar,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  Clock,
  BarChart3,
  RefreshCw,
  ChevronDown,
  User,
  List,
  PieChart,
  Users,
  GitBranch,
  Headphones,
  GitBranchPlus,
  Search,
  Banknote,
  Activity,
  CalendarClock,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Loader } from '@/components/ui/Loader';
import { fetchExtensions } from '@/services/api';
import type { Extension, ExtensionReportData } from '@/types';
import { loadCallSummary } from '@/lib/reports/extensionReports';
import toast from 'react-hot-toast';
import { loadPdfAssets } from '@/lib/pdf/pdfAssets';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { buildCallSummaryPdf } from './extensionPdfs';
import { ExtensionDetailReport } from './ExtensionDetailReport';
import { CallCostReport } from './CallCostReport';
import { ExtensionDirectoryReport } from './ExtensionDirectoryReport';
import { InboundRoutingReport } from './InboundRoutingReport';
import { OutboundRoutingReport } from './OutboundRoutingReport';
import { UnreturnedMissedCallReport } from './UnreturnedMissedCallReport';
import { QueueMissedCallReport } from './QueueMissedCallReport';
import { FailoverFollowThroughReport } from './FailoverFollowThroughReport';
import { MonthlyChart } from './MonthlyChart';
import { DailyActivityReport } from './DailyActivityReport';
import { ScheduledReports } from './ScheduledReports';
import { ScheduleReportButton } from './ScheduleReportButton';

type ReportType =
  | 'activity'
  | 'scheduled'
  | 'summary'
  | 'detail'
  | 'costs'
  | 'extensions'
  | 'inbound'
  | 'outbound'
  | 'missed'
  | 'queueMissed'
  | 'failover';

function formatLocalHtmlDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function ReportingLayout() {
  const { session } = useModuleAccess('reporting');
  const [reportType, setReportType] = useState<ReportType>('activity');
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [selectedExtension, setSelectedExtension] = useState<Extension | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingExtensions, setLoadingExtensions] = useState(true);
  const [reportData, setReportData] = useState<ExtensionReportData | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showExtensionDropdown, setShowExtensionDropdown] = useState(false);
  const [extensionSearch, setExtensionSearch] = useState('');
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadExtensions();
  }, []);

  const loadExtensions = async () => {
    try {
      setLoadingExtensions(true);
      const exts = await fetchExtensions();
      setExtensions(
        [...exts].sort((a, b) =>
          a.number.localeCompare(b.number, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
      );
    } catch (error: any) {
      toast.error('Failed to load extensions');
      console.error(error);
    } finally {
      setLoadingExtensions(false);
    }
  };

  const filteredExtensions = extensions.filter((extension) => {
    const query = extensionSearch.trim().toLowerCase();
    if (!query) return true;
    return [
      extension.number,
      extension.display_name,
      extension.username,
      extension.first_name,
      extension.last_name,
    ].some((value) => value?.toLowerCase().includes(query));
  });

  const generateReport = async () => {
    if (!selectedExtension) {
      toast.error('Please select an extension');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date must be before end date');
      return;
    }

    setLoading(true);
    try {
      const report = await loadCallSummary(selectedExtension, startDate, endDate);
      setReportData(report);
      toast.success('Report generated successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const exportToPdf = async () => {
    if (!reportData) return;

    setExportingPdf(true);
    try {
      const pdf = buildCallSummaryPdf({
        report: reportData,
        customerName: session?.customer?.name,
        assets: await loadPdfAssets(),
      });

      const extNameFile = reportData.extension.display_name || reportData.extension.number;
      const fileName = `Extension_Report_${extNameFile}_${reportData.period.startDate}_to_${reportData.period.endDate}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exported successfully!');
    } catch (error: any) {
      toast.error('Failed to export PDF');
      console.error(error);
    } finally {
      setExportingPdf(false);
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds}s`;
    }
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  // Format duration as HH:MM:SS (like Yeastar)
  const formatDurationHMS = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      {/* Report Type Selector */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <BarChart3 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Reports
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Daily activity, call statistics, call costs, missed-call follow-up, queue disposition,
              failover follow-through and routing, and reports emailed on a schedule
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setReportType('activity')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'activity'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <Activity className="w-4 h-4" />
            Daily Activity
          </button>
          <button
            onClick={() => setReportType('summary')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'summary'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <PieChart className="w-4 h-4" />
            Call Summary
          </button>
          <button
            onClick={() => setReportType('detail')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'detail'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <List className="w-4 h-4" />
            Call Detail
          </button>
          <button
            onClick={() => setReportType('costs')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'costs'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <Banknote className="w-4 h-4" />
            Call Costs
          </button>
          <button
            onClick={() => setReportType('missed')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'missed'
                ? 'bg-orange-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <PhoneMissed className="w-4 h-4" />
            Unreturned Missed
          </button>
          <button
            onClick={() => setReportType('queueMissed')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'queueMissed'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <Headphones className="w-4 h-4" />
            Queue Missed
          </button>
          <button
            onClick={() => setReportType('failover')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'failover'
                ? 'bg-violet-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <GitBranchPlus className="w-4 h-4" />
            Failover Follow-through
          </button>
          <button
            onClick={() => setReportType('extensions')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'extensions'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <Users className="w-4 h-4" />
            Extension Directory
          </button>
          <button
            onClick={() => setReportType('inbound')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'inbound'
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <GitBranch className="w-4 h-4" />
            Inbound Routing
          </button>
          <button
            onClick={() => setReportType('outbound')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'outbound'
                ? 'bg-teal-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <PhoneOutgoing className="w-4 h-4" />
            Outbound Routing
          </button>
          <button
            onClick={() => setReportType('scheduled')}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
              reportType === 'scheduled'
                ? 'bg-primary-600 text-white'
                : 'bg-primary-50 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 hover:bg-primary-100 dark:hover:bg-primary-900/50'
            }`}
          >
            <CalendarClock className="w-4 h-4" />
            Scheduled
          </button>
        </div>
      </Card>

      {/* Conditional Report Content */}
      {reportType === 'activity' ? (
        <DailyActivityReport />
      ) : reportType === 'scheduled' ? (
        <ScheduledReports />
      ) : reportType === 'detail' ? (
        <ExtensionDetailReport />
      ) : reportType === 'costs' ? (
        <CallCostReport />
      ) : reportType === 'missed' ? (
        <UnreturnedMissedCallReport />
      ) : reportType === 'queueMissed' ? (
        <QueueMissedCallReport />
      ) : reportType === 'failover' ? (
        <FailoverFollowThroughReport />
      ) : reportType === 'extensions' ? (
        <ExtensionDirectoryReport />
      ) : reportType === 'inbound' ? (
        <InboundRoutingReport />
      ) : reportType === 'outbound' ? (
        <OutboundRoutingReport />
      ) : (
      <>
      {/* Report Configuration Card */}
      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <FileText className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
              Extension Summary Report
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Generate monthly call statistics for any extension
            </p>
          </div>
          {selectedExtension && (
            <ScheduleReportButton
              kind="summary"
              className="ml-auto"
              filters={{ extension: selectedExtension.number }}
              name={`Call Summary ${selectedExtension.number}`}
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {/* Extension Selector */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Extension
            </label>
            {loadingExtensions ? (
              <div className="h-10 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setShowExtensionDropdown(!showExtensionDropdown);
                    if (showExtensionDropdown) setExtensionSearch('');
                  }}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-left text-gray-900 dark:text-white hover:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    {selectedExtension
                      ? `${selectedExtension.number} - ${selectedExtension.display_name || selectedExtension.username || 'Unknown'}`
                      : 'Select extension...'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showExtensionDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExtensionDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute z-20 mt-1 w-full min-w-[280px] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg overflow-hidden"
                  >
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        <input
                          type="search"
                          autoFocus
                          value={extensionSearch}
                          onChange={(event) => setExtensionSearch(event.target.value)}
                          placeholder="Search extension..."
                          className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-md text-sm text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {filteredExtensions.length ? (
                        filteredExtensions.map((ext) => (
                          <button
                            type="button"
                            key={ext.id}
                            onClick={() => {
                              setSelectedExtension(ext);
                              setShowExtensionDropdown(false);
                              setExtensionSearch('');
                            }}
                            className={`w-full px-4 py-2.5 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 ${
                              selectedExtension?.id === ext.id
                                ? 'bg-primary-50 dark:bg-primary-900/30'
                                : ''
                            }`}
                          >
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-gray-900 dark:text-white font-medium">
                              {ext.number}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400">
                              - {ext.display_name || ext.username || 'Unknown'}
                            </span>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                          No extensions found
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex items-end">
            <Button
              onClick={generateReport}
              disabled={loading || !selectedExtension || !startDate || !endDate}
              className="w-full"
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Quick Date Presets */}
        <div className="flex flex-wrap gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400 mr-2">Quick select:</span>
          {[
            { label: 'This Month', start: () => {
              const now = new Date();
              return formatLocalHtmlDate(new Date(now.getFullYear(), now.getMonth(), 1));
            }, end: () => formatLocalHtmlDate(new Date()) },
            { label: 'Last Month', start: () => {
              const now = new Date();
              return formatLocalHtmlDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
            }, end: () => {
              const now = new Date();
              return formatLocalHtmlDate(new Date(now.getFullYear(), now.getMonth(), 0));
            }},
            { label: 'This Year', start: () => {
              const now = new Date();
              return `${now.getFullYear()}-01-01`;
            }, end: () => formatLocalHtmlDate(new Date()) },
            { label: 'Last Year', start: () => {
              const now = new Date();
              return `${now.getFullYear() - 1}-01-01`;
            }, end: () => {
              const now = new Date();
              return `${now.getFullYear() - 1}-12-31`;
            }},
          ].map((preset) => (
            <button
              key={preset.label}
              onClick={() => {
                setStartDate(preset.start());
                setEndDate(preset.end());
              }}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </Card>

      {/* Loading State */}
      {loading && (
        <Card>
          <div className="flex flex-col items-center justify-center py-12">
            <Loader text="Generating report..." />
            <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
              This may take a moment for large date ranges
            </p>
          </div>
        </Card>
      )}

      {/* Report Results */}
      {reportData && !loading && (
        <div ref={reportRef} className="space-y-6 bg-white dark:bg-gray-900 p-1">
          {/* Report Header */}
          <Card>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Report: Extension {reportData.extension.number}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {reportData.extension.display_name || reportData.extension.username || 'Unknown'}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  Period: {new Date(reportData.period.startDate).toLocaleDateString()} - {new Date(reportData.period.endDate).toLocaleDateString()}
                </p>
              </div>
              <Button
                onClick={exportToPdf}
                disabled={exportingPdf}
                variant="secondary"
                className="pdf-hide"
              >
                {exportingPdf ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export to PDF
                  </>
                )}
              </Button>
            </div>
          </Card>

          {/* Summary Stats - Yeastar Style */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Inbound Summary */}
            <Card>
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 sm:p-3 rounded-xl flex-shrink-0 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                  <PhoneIncoming className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Inbound</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.summary.inboundTotal}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                  <span className="text-gray-500 dark:text-gray-400">Answered</span>
                  <p className="font-semibold text-green-600 dark:text-green-400">{reportData.summary.inboundAnswered}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                  <span className="text-gray-500 dark:text-gray-400">No Answer</span>
                  <p className="font-semibold text-red-600 dark:text-red-400">{reportData.summary.inboundNoAnswer}</p>
                </div>
              </div>
            </Card>

            {/* Outbound Summary */}
            <Card>
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 sm:p-3 rounded-xl flex-shrink-0 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400">
                  <PhoneOutgoing className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Outbound</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{reportData.summary.outboundTotal}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                  <span className="text-gray-500 dark:text-gray-400">Answered</span>
                  <p className="font-semibold text-green-600 dark:text-green-400">{reportData.summary.outboundAnswered}</p>
                </div>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-2">
                  <span className="text-gray-500 dark:text-gray-400">No Answer</span>
                  <p className="font-semibold text-red-600 dark:text-red-400">{reportData.summary.outboundNoAnswer}</p>
                </div>
              </div>
            </Card>

            {/* Talk Time Summary */}
            <Card>
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 sm:p-3 rounded-xl flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Talk Duration</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatDurationHMS(reportData.summary.totalTalkDuration)}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2">
                  <span className="text-gray-500 dark:text-gray-400">Avg Duration</span>
                  <p className="font-semibold text-purple-600 dark:text-purple-400">{formatDuration(reportData.summary.averageCallDuration)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-2">
                  <span className="text-gray-500 dark:text-gray-400">Total Answered</span>
                  <p className="font-semibold text-gray-900 dark:text-white">{reportData.summary.inboundAnswered + reportData.summary.outboundAnswered}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Monthly Comparison Chart */}
          {reportData.monthlyData.length > 0 && (
            <Card>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                Monthly Activity Comparison
              </h4>
              <MonthlyChart data={reportData.monthlyData} />
            </Card>
          )}

          {/* Monthly Details Table - Yeastar Style */}
          {reportData.monthlyData.length > 0 && (
            <Card>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Monthly Breakdown
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                      <th rowSpan={2} className="text-left py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300 align-bottom">
                        Month
                      </th>
                      <th colSpan={3} className="text-center py-2 px-3 text-sm font-semibold text-green-700 dark:text-green-400 border-b border-gray-200 dark:border-gray-700">
                        Inbound
                      </th>
                      <th colSpan={3} className="text-center py-2 px-3 text-sm font-semibold text-primary-700 dark:text-primary-400 border-b border-gray-200 dark:border-gray-700">
                        Outbound
                      </th>
                      <th rowSpan={2} className="text-center py-2 px-3 text-sm font-semibold text-gray-700 dark:text-gray-300 align-bottom">
                        Total Talk<br />Duration
                      </th>
                    </tr>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">Answered</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">No Answer</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">Total</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">Answered</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">No Answer</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.monthlyData.map((month, index) => (
                      <motion.tr
                        key={month.monthKey}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                      >
                        <td className="py-3 px-3 font-medium text-gray-900 dark:text-white">
                          {month.month}
                        </td>
                        {/* Inbound */}
                        <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                          {month.inboundAnswered}
                        </td>
                        <td className="py-3 px-2 text-center text-red-500 dark:text-red-400">
                          {month.inboundNoAnswer}
                        </td>
                        <td className="py-3 px-2 text-center font-medium text-gray-700 dark:text-gray-300">
                          {month.inboundTotal}
                        </td>
                        {/* Outbound */}
                        <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                          {month.outboundAnswered}
                        </td>
                        <td className="py-3 px-2 text-center text-red-500 dark:text-red-400">
                          {month.outboundNoAnswer}
                        </td>
                        <td className="py-3 px-2 text-center font-medium text-gray-700 dark:text-gray-300">
                          {month.outboundTotal}
                        </td>
                        {/* Duration */}
                        <td className="py-3 px-3 text-center font-medium text-gray-900 dark:text-white">
                          {formatDurationHMS(month.totalTalkDuration)}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold border-t-2 border-gray-300 dark:border-gray-600">
                      <td className="py-3 px-3 text-gray-900 dark:text-white">Total</td>
                      {/* Inbound Totals */}
                      <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                        {reportData.summary.inboundAnswered}
                      </td>
                      <td className="py-3 px-2 text-center text-red-500 dark:text-red-400">
                        {reportData.summary.inboundNoAnswer}
                      </td>
                      <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">
                        {reportData.summary.inboundTotal}
                      </td>
                      {/* Outbound Totals */}
                      <td className="py-3 px-2 text-center text-green-600 dark:text-green-400">
                        {reportData.summary.outboundAnswered}
                      </td>
                      <td className="py-3 px-2 text-center text-red-500 dark:text-red-400">
                        {reportData.summary.outboundNoAnswer}
                      </td>
                      <td className="py-3 px-2 text-center text-gray-700 dark:text-gray-300">
                        {reportData.summary.outboundTotal}
                      </td>
                      {/* Duration Total */}
                      <td className="py-3 px-3 text-center text-gray-900 dark:text-white">
                        {formatDurationHMS(reportData.summary.totalTalkDuration)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </Card>
          )}

          {/* Report Footer */}
          <Card>
            <div className="text-center text-sm text-gray-500 dark:text-gray-400">
              <p>Report generated on {new Date().toLocaleString()}</p>
              <p className="mt-1">Yeastarify - Extension Detail Report</p>
            </div>
          </Card>
        </div>
      )}

      {/* Empty State */}
      {!reportData && !loading && (
        <Card>
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
              <BarChart3 className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Report Generated
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
              Select an extension and date range above, then click "Generate Report" to view detailed call statistics.
            </p>
          </div>
        </Card>
      )}
      </>
      )}
    </div>
  );
}
